
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { supabase } from '../../../lib/supabaseClient';
import { setLoading as setGlobalLoading } from '../../../store/slices/authSlice';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { handleEdgeFunctionError } from '../../../utils/edgeFunctionUtils';
import {
  EmployerSigninRequest,
  SendRegistrationEmailRequest,
} from '../types';
import { AUTH_ERRORS } from '../../../constants';

export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const login = useCallback(async (email: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    dispatch(setGlobalLoading(true));
    try {
      const { data, error } = await supabase.functions.invoke('employer-signin', {
        body: { email: email.toLowerCase() } as EmployerSigninRequest,
      });

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }

      // Check for application level error in 200 OK response (as per contract)
      if (data?.error) {
        const message = data.error.message || '';
        if (message.toLowerCase().includes('not registered')) {
          return { success: false, error: AUTH_ERRORS.REGISTRATION_DISABLED };
        }
        throw new Error(message || AUTH_ERRORS.GENERIC_ERROR);
      }

      return { success: true };
    } catch (err: any) {
      showSnackbar(AUTH_ERRORS.GENERIC_ERROR, 'error');
      return { success: false, error: AUTH_ERRORS.GENERIC_ERROR };
    } finally {
      setLoading(false);
      dispatch(setGlobalLoading(false));
    }
  }, [dispatch, showSnackbar]);

  return { login, loading };
};

export const useRegister = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const register = useCallback(async (payload: SendRegistrationEmailRequest): Promise<boolean> => {
    setLoading(true);
    dispatch(setGlobalLoading(true));
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-email', {
        body: payload,
      });

      if (error) {
        const extractedError = await handleEdgeFunctionError(error);
        throw extractedError;
      }
      if (data?.error) throw new Error(data.error.message || 'Failed to send registration email');

      return true;
    } catch (err: any) {
      console.error('Registration error:', err);
      showSnackbar('Failed to send registration email', 'error');
      return false;
    } finally {
      setLoading(false);
      dispatch(setGlobalLoading(false));
    }
  }, [dispatch, showSnackbar]);

  return { register, loading };
};

export const useVerifyOtp = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const verifyOtp = useCallback(async (email: string, token: string): Promise<boolean> => {
    setLoading(true);
    dispatch(setGlobalLoading(true));
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) throw error;

      return true;
    } catch (err: any) {
      console.error('Verify OTP error:', err);
      showSnackbar(AUTH_ERRORS.INVALID_OTP, 'error');
      return false;
    } finally {
      setLoading(false);
      dispatch(setGlobalLoading(false));
    }
  }, [dispatch, showSnackbar]);

  return { verifyOtp, loading };
};

export const useResendOtp = () => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const resendOtp = useCallback(async (email: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
      });

      if (error) throw error;

      showSnackbar('OTP resent successfully', 'success');
      return true;
    } catch (err: any) {
      console.error('Resend OTP error:', err);
      showSnackbar(AUTH_ERRORS.RESEND_FAILED, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  return { resendOtp, loading };
};
